import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { getDashboardData, getOrganizationByUser, getUser } from '../services/api';

// Sparkline component for the metric cards
const Sparkline = ({ data, color }) => (
  <div className="absolute bottom-0 right-0 w-32 h-16 opacity-30">
    <LineChart width={128} height={64} data={data}>
      <YAxis hide domain={['auto', 'auto']} />
      <Line
        type="monotone"
        dataKey="value"
        stroke={color}
        strokeWidth={2}
        dot={false}
        isAnimationActive={false}
      />
    </LineChart>
  </div>
);

// Metric card component
const MetricCard = ({ title, value, change, sparkData, color, trend }) => (
  <div className="bg-gray-800 rounded-xl p-4 relative overflow-hidden hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300">
    <div className="relative z-10">
      <h3 className="text-gray-400 text-sm font-medium mb-1">{title}</h3>
      <div className="flex items-baseline gap-2">
        <p className="text-white text-2xl font-bold">{value}</p>
        <span className={`text-sm font-medium ${trend === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
          {change}
        </span>
      </div>
    </div>
    <Sparkline data={sparkData} color={color} />
  </div>
);

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [organization, setOrganization] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const user = getUser();
        if (!user) {
          setError('User not found. Please log in again.');
          setLoading(false);
          return;
        }
        
        const orgData = await getOrganizationByUser(user);
        setOrganization(orgData);
        
        if (orgData && orgData.id) {
          const data = await getDashboardData(orgData.id);
          setDashboardData(data);
        } else {
          setError('Organization not found');
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  // Generate sparkline data from chart data
  const generateSparklineData = (dataKey) => {
    if (!dashboardData || !dashboardData.chartData) return [];
    
    // Transform chart data to sparkline format
    return dashboardData.chartData.map(item => {
      if (dataKey === 'calls') {
        return { value: item.calls || 0 };
      } else if (dataKey === 'minutes') {
        return { value: item.minutes || 0 };
      } /*else if (dataKey === 'cost') {
        return { value: item.cost || 0 };
      }*/ else if (dataKey === 'appointments') {
        return { value: item.appointments || 0 };
      }
      return { value: 0 };
    });
  };

  // Format currency values
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        <p>{error}</p>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="bg-gray-100 border border-gray-400 text-gray-700 px-4 py-3 rounded mb-4">
        <p>No dashboard data available</p>
      </div>
    );
  }

  const { stats, chartData, callMetrics } = dashboardData;

  // Calculate change percentages based on period growth
  const getChangePercentage = (value, isPositiveGood = true) => {
    const growth = callMetrics?.periodGrowth || 0;
    const sign = growth > 0 ? '+' : '';
    const formattedGrowth = `${sign}${growth.toFixed(2)}%`;
    
    // For metrics where lower is better (like cost per call), invert the trend
    const trend = isPositiveGood ? (growth >= 0 ? 'up' : 'down') : (growth >= 0 ? 'down' : 'up');
    
    return { formattedGrowth, trend };
  };

  // Prepare metrics data
  const metricsData = [
    {
      title: "Total Call Minutes",
      value: stats.totalCallMinutes.toFixed(0),
      change: getChangePercentage(stats.totalCallMinutes).formattedGrowth,
      sparkData: generateSparklineData('minutes'),
      color: "rgb(45, 212, 191)",
      trend: getChangePercentage(stats.totalCallMinutes).trend
    },
    {
      title: "Number of Calls",
      value: stats.totalCalls.toString(),
      change: getChangePercentage(stats.totalCalls).formattedGrowth,
      sparkData: generateSparklineData('calls'),
      color: "rgb(99, 102, 241)",
      trend: getChangePercentage(stats.totalCalls).trend
    }/*,
    {
      title: "Total Spent",
      value: formatCurrency(stats.totalCost),
      change: getChangePercentage(stats.totalCost, false).formattedGrowth,
      sparkData: generateSparklineData('cost'),
      color: "rgb(249, 115, 22)",
      trend: getChangePercentage(stats.totalCost, false).trend
    },
    {
      title: "Average Cost per Call",
      value: formatCurrency(stats.avgCostPerCall),
      change: getChangePercentage(stats.avgCostPerCall, false).formattedGrowth,
      sparkData: generateSparklineData('calls').map((d, i, arr) => {
        // Create a slightly variable cost per call for the sparkline
        const variation = Math.sin(i * 0.5) * 0.05;
        return { value: stats.avgCostPerCall * (1 + variation) };
      }),
      color: "rgb(34, 197, 94)",
      trend: getChangePercentage(stats.avgCostPerCall, false).trend
    }*/
  ];

  // Format chart data for Recharts
  const formattedChartData = chartData.map(item => ({
    period: item.period,
    calls: item.calls || 0,
    appointments: item.appointments || 0,
    minutes: item.minutes || 0,
    cost: item.cost || 0
  }));

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Organization Info */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-200 mb-2">
          {organization ? organization.name : 'Dashboard'}
        </h2>
        <p className="text-gray-400">
          Analytics and insights for your voice assistant
        </p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-900 rounded-xl shadow-lg shadow-blue-500/20 p-6 transform transition-all duration-300 hover:scale-105">
          <h3 className="text-xl font-semibold text-gray-100 mb-2">Active Assistants</h3>
          <p className="text-4xl font-bold text-blue-400">{stats.activeAssistants || 0}</p>
        </div>

        <div className="bg-gray-900 rounded-xl shadow-lg shadow-purple-500/20 p-6 transform transition-all duration-300 hover:scale-105">
          <h3 className="text-xl font-semibold text-gray-100 mb-2">Total Contacts</h3>
          <p className="text-4xl font-bold text-purple-400">{stats.totalContacts || 0}</p>
        </div>

        <div className="bg-gray-900 rounded-xl shadow-lg shadow-green-500/20 p-6 transform transition-all duration-300 hover:scale-105">
          <h3 className="text-xl font-semibold text-gray-100 mb-2">Scheduled Appointments</h3>
          <p className="text-4xl font-bold text-green-400">{stats.scheduledAppointments || 0}</p>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {metricsData.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Graphs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-900 rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-100 mb-4">Calls Over Time</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={formattedChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="period" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="calls" 
                  name="Calls"
                  stroke="#8B5CF6" 
                  strokeWidth={2}
                  dot={{ stroke: '#8B5CF6', strokeWidth: 2 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="appointments" 
                  name="Appointments"
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={{ stroke: '#10B981', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-gray-900 rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-100 mb-4">Call Minutes</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={formattedChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="period" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Legend />
                <Bar 
                  dataKey="minutes" 
                  name="Call Minutes"
                  fill="#F59E0B" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Calls */}
      <div className="bg-gray-900 rounded-xl shadow-lg p-6 mb-8">
        <h3 className="text-xl font-semibold text-gray-100 mb-4">Recent Calls</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-300">
            <thead className="text-xs text-gray-400 uppercase bg-gray-800">
              <tr>
                <th scope="col" className="px-6 py-3">Date</th>
                <th scope="col" className="px-6 py-3">Assistant</th>
                <th scope="col" className="px-6 py-3">Duration</th>
	        {/*<th scope="col" className="px-6 py-3">Cost</th>*/}
                <th scope="col" className="px-6 py-3">Type</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.recentCalls && dashboardData.recentCalls.map((call, index) => (
                <tr key={index} className="border-b border-gray-700 hover:bg-gray-800">
                  <td className="px-6 py-4">
                    {new Date(call.startTime).toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    {call.assistantResponse?.assistantId || 'Unknown'}
                  </td>
                  <td className="px-6 py-4">
                    {call.durationInMinutes ? `${call.durationInMinutes.toFixed(1)} min` : 'N/A'}
                  </td>
		  {/*<td className="px-6 py-4">
                    {call.cost ? formatCurrency(call.cost) : 'N/A'}
                  </td>
		  */}
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      call.callType === 'INBOUND' 
                        ? 'bg-green-900 text-green-200' 
                        : 'bg-blue-900 text-blue-200'
                    }`}>
                      {call.callType || 'Unknown'}
                    </span>
                  </td>
                </tr>
              ))}
              {(!dashboardData.recentCalls || dashboardData.recentCalls.length === 0) && (
                <tr className="border-b border-gray-700">
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-400">
                    No call records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Call Statistics */}
      <div className="space-y-6">
        <div className="bg-gray-900 rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-100 mb-4">Call Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="text-gray-400 mb-1">Avg. Call Duration</h4>
              <p className="text-2xl font-bold text-white">
                {stats.totalCalls > 0
                  ? `${(stats.totalCallMinutes / stats.totalCalls).toFixed(1)} min`
                  : '0 min'}
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="text-gray-400 mb-1">Success Rate</h4>
              <p className="text-2xl font-bold text-white">98.2%</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="text-gray-400 mb-1">Conversion Rate</h4>
              <p className="text-2xl font-bold text-white">
                {formattedChartData && formattedChartData.length > 0 && 
                  formattedChartData.reduce((sum, item) => sum + item.calls, 0) > 0
                  ? `${((formattedChartData.reduce((sum, item) => sum + (item.appointments || 0), 0) / 
                         formattedChartData.reduce((sum, item) => sum + item.calls, 0)) * 100).toFixed(1)}%`
                  : '0%'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
